import React from "react";
import { useTranslation } from "react-i18next";
import { DATE_PATTERN_DD_MMM_YYYY_NODASH, convertToDateJakartaTime, toIndoFormat } from "../../helper/helper";
import { FruitTraceability } from "../../models/traceability/FruitTraceability";
import { ChevronRightIcon, HelpCircleIcon } from "./PurchaseReportIcons";
import { deforestationPillClass, getTraceabilityData } from "./traceabilityTypes";

type Props = {
    contract: FruitTraceability
    onDetailClick: (contract: FruitTraceability) => void
    onContractClick: (contract: FruitTraceability) => void
    highlightedFromMap: boolean
    /** This contract is the one the map is focused on. */
    isSelected: boolean
    /** The map has been widened, so the row sheds its lower-priority columns. */
    compact: boolean
}

export const PurchaseReportRow: React.FC<Props> = ({
    contract,
    onDetailClick,
    onContractClick,
    highlightedFromMap,
    isSelected,
    compact }) => {

    const { t } = useTranslation()

    const traceData = getTraceabilityData(contract.traceabilityLabel())
    const sourceCount = contract.sourceCount()

    const sourcesCell = sourceCount > 0
        ? <span className="ds-pill ds-pill--count">{sourceCount}</span>
        : <span className="ds-pill ds-pill--unknown">
            <HelpCircleIcon color="var(--ds-dashed-text)" />
            {!compact && t('unknown')}
        </span>

    return (
        <div
            className={`ds-grid-table__row ${highlightedFromMap ? 'pem-row-highlight' : ''} ${isSelected ? 'ds-grid-table__row--selected' : ''}`}
            data-rowid={contract.contract_id}
            data-contract-id={contract.contract_id}
            onClick={(e) => {
                onContractClick(contract)
                e.stopPropagation()
            }}
            style={{ cursor: 'pointer' }}
        >
            {/* Compact has no room for the traceability pill, so the id carries its dot. */}
            <span
                className="ds-grid-table__cell--num"
                style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6 }}
            >
                {compact && <span className={`ds-trace-dot ${traceData.dotClass}`} title={traceData.label} />}
                {contract.contract_id}
            </span>

            <span>
                {contract.market_day
                    ? convertToDateJakartaTime(contract.market_day, DATE_PATTERN_DD_MMM_YYYY_NODASH)
                    : ""}
            </span>

            {!compact && (
                <span style={{ display: 'flex', paddingRight: 12, minWidth: 0 }}>
                    <span className={`ds-pill ds-pill--trace ${traceData.pillClass}`}>
                        <span className="ds-pill__dot" />
                        {traceData.label}
                    </span>
                </span>
            )}

            {!compact && (
                <span style={{ display: 'flex', paddingRight: 14 }}>
                    <span className={`ds-pill ds-pill--risk ${deforestationPillClass(contract.deforestationRawLabel())}`}>
                        {t(contract.deforestationLabel())}
                    </span>
                </span>
            )}

            <span style={{ display: 'flex', alignItems: 'center', paddingRight: 12, minWidth: 0 }}>
                {sourcesCell}
            </span>

            <span className="ds-grid-table__cell--num">{toIndoFormat(contract.quantity)}</span>

            {!compact && <span className="ds-grid-table__cell--num">{toIndoFormat(contract.price)}</span>}

            <span className="ds-grid-table__cell--end">
                <button
                    className={`ds-btn ds-btn--sm ${compact ? '' : 'ds-btn--primary'}`}
                    title={t('detail')}
                    onClick={(e) => { onDetailClick(contract); e.stopPropagation() }}
                >
                    {compact ? <ChevronRightIcon size={14} /> : t('detail')}
                </button>
            </span>
        </div>
    )
}
